// src/components/Auth/Login.tsx - VERSIÓN FINAL CON RUTAS CORRECTAS 
import React, { useState, useEffect } from 'react';
import { User, Lock, Eye, EyeOff, ArrowRight, Sparkles, CreditCard, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
// IMPORTAR IMÁGENES CON RUTAS CORRECTAS
import logoLight from '../../assets/images/logo_light.png';  // Logo claro para fondo oscuro
import logoMain from '../../assets/images/logo.png';         // Logo principal
import leftImage from '../../assets/images/left.png';        // Imagen lateral
import bgImage from '../../assets/images/bg1.jpg';            // Imagen de fondo
import iconImage from '../../assets/images/icons/icon.png';
// Componentes de autocompletado
import QuotePartsModal from '../QuotePartsModal';
import QuoteSystemsModal from '../QuoteSystemsModal';
import { ipService } from '../../services/ipService';

interface LoginProps {
  onLogin: (email: string, password: string, rememberMe: boolean) => Promise<any>;
}

interface Repuesto {
  pieza: string;
  cantidad: number;
  tipo: 'originales' | 'homologados' | 'ambos';
}

interface FormRepuestos {
  nombre: string;
  rut: string;
  correo: string;
  telefono: string;
  marca: string;
  modelo: string;
  configuracion: string;
  ano: string;
  chasis: string;
  patente: string;
  repuestos: Repuesto[];
  confirmaInfo: boolean;
  observaciones: string;
}

interface FormSistemas {
  empresa: string;
  correo: string;
  telefono: string;
  necesidad: string;
  observaciones: string;
}

// ===== AGREGAR CONSTANTES (antes del componente) =====

const NUMEROS_PARTE_COMUNES = {
  'Filtros': [
    { np: '123-4567', desc: 'Filtro de aceite primario' },
    { np: '234-5678', desc: 'Filtro de aceite secundario' },
    { np: '345-6789', desc: 'Filtro de combustible' },
    { np: '456-7890', desc: 'Filtro hidráulico' },
    { np: '567-8901', desc: 'Filtro de aire' }
  ],
  'Motor': [
    { np: '678-9012', desc: 'Bujía de precalentamiento' },
    { np: '789-0123', desc: 'Inyector' },
    { np: '890-1234', desc: 'Turbocompresor' }
  ],
  'Transmisión': [
    { np: '901-2345', desc: 'Disco de embrague' },
    { np: '012-3456', desc: 'Plato de presión' }
  ]
};

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
  const navigate = useNavigate();
  // Estados de modales
  const [showPartsModal, setShowPartsModal] = useState(false);
  const [showSystemsModal, setShowSystemsModal] = useState(false);
  
  const [showNPHelper, setShowNPHelper] = useState(false);

  // Estados de configuraciones
  const [configuracionesDisponibles, setConfiguracionesDisponibles] = useState<string[]>([]);
  const [loadingConfiguraciones, setLoadingConfiguraciones] = useState(false);

  // Estados de formularios
  const [formSistemas, setFormSistemas] = useState<FormSistemas>({
    empresa: '',
    correo: '',
    telefono: '',
    necesidad: '',
    observaciones: ''
  });

  const [formRepuestos, setFormRepuestos] = useState<FormRepuestos>({
    nombre: '',
    rut: '',
    correo: '',
    telefono: '',
    marca: 'Caterpillar',
    modelo: '',
    configuracion: '',
    ano: '',
    chasis: '',
    patente: '',
    repuestos: [{
      pieza: '',
      cantidad: 1,
      tipo: 'homologados'
    }],
    confirmaInfo: false,
    observaciones: ''
  });

  const [isSubmittingSistemas, setIsSubmittingSistemas] = useState(false);
  const [isSubmittingRepuestos, setIsSubmittingRepuestos] = useState(false);

const cargarConfiguraciones = async (modelo: string) => {
  if (!modelo) {
    setConfiguracionesDisponibles([]);
    return;
  }
  
  setLoadingConfiguraciones(true);
  
  try {
    const response = await fetch(`/api/equipment/configuraciones?modelo=${modelo}`);
    const data = await response.json();
    
    if (data.success && data.configuraciones) {
      setConfiguracionesDisponibles(data.configuraciones);
    }
  } catch (error) {
    console.error('Error cargando configuraciones:', error);
    setConfiguracionesDisponibles([]);
  } finally {
    setLoadingConfiguraciones(false);
  }
};

// Llamar cuando cambie el modelo
const handleModeloChange = (modelo: string) => {
  setFormRepuestos({
    ...formRepuestos,
    modelo: modelo,
    configuracion: '' // resetear configuración
  });
  cargarConfiguraciones(modelo);
};


// Manejo del submit del formulario
const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setError('');
  setPaymentUrl(null);

  if (!email || !password) {
    setError('Por favor completa todos los campos');
    return;
  }

  setIsLoading(true);

  try {
    console.log('🔐 [LOGIN] Iniciando login con:', { email });

    // ✅ OBTENER IP PÚBLICA DEL CLIENTE
    const publicIp = await ipService.getPublicIP();

    const deviceInfo = {
      userAgent: navigator.userAgent,
      browser: navigator.userAgentData?.brands?.[0]?.brand || 'Unknown',
      platform: navigator.platform,
      publicIp: publicIp  // ← AGREGAR IP PÚBLICA
    };

    const response = await axios.post('/auth/login', {
      email,
      password,
      deviceInfo,
    });

    const { accessToken, refreshToken, sessionId, user } = response.data;

    if (!accessToken || !user) {
      throw new Error('Respuesta inválida del servidor');
    }

    localStorage.setItem('token', accessToken);
    localStorage.setItem('kikai_token', accessToken);
    localStorage.setItem('refreshToken', refreshToken || '');
    localStorage.setItem('sessionId', sessionId || '');
    localStorage.setItem('user', JSON.stringify(user));
    
    useAuthStore.getState().setAuth(user, accessToken, refreshToken, sessionId);

    await new Promise(resolve => setTimeout(resolve, 300));

    console.log('🚀 [LOGIN] Navegando a dashboard...');
    window.location.href = '/dashboard';

  } catch (err: any) {
    console.error('Error en login:', err);
    
    if (err.response?.status === 409 && err.response?.data?.code === 'SESSION_ALREADY_ACTIVE') {
      setError('Ya tienes una sesión activa en otro dispositivo. Por favor cierra esa sesión primero o espera a que expire (15 minutos de inactividad).');
    } else {
      setError(err.response?.data?.message || 'Error al iniciar sesión');
    }
  } finally {
    setIsLoading(false);
  }
};

  
  const validarRUT = (rut: string): boolean => {
  const rutLimpio = rut.replace(/\./g, '').replace(/-/g, '');
  if (rutLimpio.length < 8) return false;
  
  const cuerpo = rutLimpio.slice(0, -1);
  const dv = rutLimpio.slice(-1).toUpperCase();
  
  let suma = 0;
  let multiplicador = 2;
  
  for (let i = cuerpo.length - 1; i >= 0; i--) {
    suma += parseInt(cuerpo[i]) * multiplicador;
    multiplicador = multiplicador === 7 ? 2 : multiplicador + 1;
  }
  
  const dvCalculado = 11 - (suma % 11);
  const dvFinal = dvCalculado === 11 ? '0' : dvCalculado === 10 ? 'K' : dvCalculado.toString();
  
  return dv === dvFinal;
};

const formatearRUT = (valor: string): string => {
  const limpio = valor.replace(/[^0-9kK]/g, '');
  if (limpio.length <= 1) return limpio;
  
  const cuerpo = limpio.slice(0, -1);
  const dv = limpio.slice(-1);
  
  const cuerpoFormateado = cuerpo.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${cuerpoFormateado}-${dv}`;
};

  // ============================================
  // MANEJO DE REPUESTOS
  // ============================================
  const agregarRepuesto = () => {
    setFormRepuestos({
      ...formRepuestos,
      repuestos: [
        ...formRepuestos.repuestos,
        { pieza: '', cantidad: 1, tipo: 'homologados' }
      ]
    });
  };

  const eliminarRepuesto = (index: number) => {
    const nuevosRepuestos = formRepuestos.repuestos.filter((_, i) => i !== index);
    setFormRepuestos({
      ...formRepuestos,
      repuestos: nuevosRepuestos.length > 0 ? nuevosRepuestos : [{ pieza: '', cantidad: 1, tipo: 'homologados' }]
    });
  };

  const actualizarRepuesto = (index: number, campo: keyof Repuesto, valor: any) => {
    const nuevosRepuestos = [...formRepuestos.repuestos];
    nuevosRepuestos[index] = { ...nuevosRepuestos[index], [campo]: valor };
    setFormRepuestos({ ...formRepuestos, repuestos: nuevosRepuestos });
  };

  const handleSistemasSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmittingSistemas(true);
    
    try {
      const response = await fetch('/api/contact/sistemas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formSistemas)
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('✅ Solicitud enviada correctamente. Te contactaremos pronto.');
        setShowSistemasModal(false);
        setFormSistemas({ empresa: '', correo: '', telefono: '', necesidad: '', observaciones: '' });
      } else {
        alert(data.message || 'Error enviando solicitud');
      }
    } catch (error) {
      alert('Error enviando solicitud. Intenta nuevamente.');
    } finally {
      setIsSubmittingSistemas(false);
    }
  };

  const handleRepuestosSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validarRUT(formRepuestos.rut)) {
      alert('RUT inválido. Verifica el número.');
      return;
    }
    
    if (!formRepuestos.confirmaInfo) {
      alert('Debes confirmar que la información es correcta.');
      return;
    }
    
    if (formRepuestos.repuestos.some(r => !r.pieza || r.cantidad <= 0)) {
      alert('Completa todos los repuestos o elimínalos.');
      return;
    }
    
    setIsSubmittingRepuestos(true);
    
    try {
      const response = await fetch('/api/contact/repuestos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formRepuestos)
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('✅ Cotización enviada. Te contactaremos pronto.');
        setShowRepuestosModal(false);
        setFormRepuestos({
          nombre: '', rut: '', correo: '', telefono: '',
          marca: 'Caterpillar', modelo: '', configuracion: '',
          ano: '', chasis: '', patente: '',
          repuestos: [{ pieza: '', cantidad: 1, tipo: 'homologados' }],
          confirmaInfo: false, observaciones: ''
        });
      } else {
        alert(data.message || 'Error enviando cotización');
      }
    } catch (error) {
      alert('Error enviando cotización. Intenta nuevamente.');
    } finally {
      setIsSubmittingRepuestos(false);
    }
  };

  // Manejo de animaciones de inputs
  useEffect(() => {
    const handleInputFocus = (e: Event) => {
      const target = e.target as HTMLInputElement;
      const parent = target.closest('.input-div');
      if (parent) parent.classList.add('focus');
    };

    const handleInputBlur = (e: Event) => {
      const target = e.target as HTMLInputElement;
      const parent = target.closest('.input-div');
      if (parent && !target.value) parent.classList.remove('focus');
    };

    const checkInitialValues = () => {
      const inputs = document.querySelectorAll('.login-input');
      inputs.forEach((input) => {
        const inputElement = input as HTMLInputElement;
        const parent = inputElement.closest('.input-div');
        if (parent && inputElement.value) parent.classList.add('focus');
      });
    };

    const inputs = document.querySelectorAll('.login-input');
    inputs.forEach((input) => {
      input.addEventListener('focus', handleInputFocus);
      input.addEventListener('blur', handleInputBlur);
    });

    checkInitialValues();

    return () => {
      inputs.forEach((input) => {
        input.removeEventListener('focus', handleInputFocus);
        input.removeEventListener('blur', handleInputBlur);
      });
    };
  }, []);

  return (
    <>
      {/* Estilos CSS embebidos - Replicando exactamente el style.css */}
      <style>{`
        * {
          padding: 0;
          margin: 0;
          box-sizing: border-box;
        }

        .login-container {
          position: relative;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          font-family: 'Saira';
          font-size: 1rem;
          font-weight: 400;
          line-height: 1.5;
          color: #333;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        .btn-payment {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 50px;
  border-radius: 25px;
  outline: none;
  border: 2px solid #fe7217;
  background: transparent;
  font-size: 0.95rem;
  font-weight: 600;
  color: #fe7217;
  text-transform: uppercase;
  margin-top: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  letter-spacing: 1px;
}

.btn-payment:hover {
  background: #fe7217;
  color: #000;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(254, 114, 23, 0.4);
}

.btn-payment svg {
  width: 20px;
  height: 20px;
}
        .login-box {
          display: flex;
          flex: 1;
          padding: 2em 5em;
          position: relative;
          z-index: 1;
        }

        /* Fondo con imagen - usando bg.jpg */
        .login-container::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image: url(${bgImage});
          background-position: center;
          background-size: cover;
          background-repeat: no-repeat;
          z-index: -1;
          filter: brightness(20.7%) contrast(99.5%) saturate(0%) blur(0px) hue-rotate(0deg);
          transition: background 0.3s, border-radius 0.3s, opacity 0.3s;
        }

        /* Overlay oscuro */
        .login-container::after {
          content: '';
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.45);
          z-index: -1;
        }

        /* Columna izquierda - Imagen */
        .login-img {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          flex: 1;
        }

        .login-img img {
          max-width: 100%;
          height: auto;
          object-fit: contain;
        }

        /* Columna derecha - Formulario */
        .login-form {
          display: flex;
          justify-content: center;
          align-items: center;
          text-align: center;
          padding-right: 200px;
          flex: 1;
        }

        .login-content {
          display: flex;
          justify-content: flex-start;
          align-items: center;
          text-align: center;
        }

        img, svg, video, canvas, audio, iframe, embed, object {
        display: inline-block !important;
        vertical-align: middle;
        }
        /* Logo */
        .logo {
          width: 100%;
          text-align: center;
          display: inline-block;
          vertical-align: middle;
        }

        .logo img {
          height: 100%;
          max-width: 100%;
          object-fit: contain;
          
        }

        /* Formulario */
        .form-width {
          width: 340px;
        }

        .title {
          color: #ADA4A4;
          margin-bottom: 30px;
          font-size: 0.9rem;
          text-transform: uppercase;
          font-weight: 600;
          letter-spacing: 1px;
        }

        /* Inputs estilizados - Replicando exactamente el CSS original */
        .input-div {
          position: relative;
          display: grid;
          grid-template-columns: 7% 93%;
          margin: 25px 0;
          padding: 5px 0;
          border-bottom: 2px solid #fe7217;
        }

        .input-div.one {
          margin-top: 0;
        }

        .i {
          color: #d9d9d9;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .i svg {
          transition: color 0.3s ease;
          width: 20px;
          height: 20px;
        }

        .input-div > div {
          position: relative;
          height: 45px;
        }

        .input-div > div > h5 {
          position: absolute;
          left: 10px;
          top: 50%;
          transform: translateY(-50%);
          color: #ADA4A4;
          font-size: 1.1rem;
          transition: all 0.3s ease;
          pointer-events: none;
          font-weight: 400;
        }

        /* Animaciones de línea inferior */
        .input-div:before, 
        .input-div:after {
          content: '';
          position: absolute;
          bottom: -2px;
          width: 0%;
          height: 2px;
          background-color: #fce148;
          transition: all 0.4s ease;
        }

        .input-div:before {
          right: 50%;
        }

        .input-div:after {
          left: 50%;
        }

        .input-div.focus:before, 
        .input-div.focus:after {
          width: 50%;
        }

        .input-div.focus > div > h5 {
          top: -15px;
          font-size: 0.8rem;
          color: #fce148;
        }

        .input-div.focus > .i > svg {
          color: #fe7217;
        }

        /* Input field */
        .input-div > div > input {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          height: 100%;
          border: none;
          outline: none;
          background: none;
          padding: 0.5rem 0.7rem;
          font-size: 1.1rem;
          color: #fff;
          font-family: 'Saira', sans-serif;
        }

        .login-input {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          height: 100%;
          border: none;
          outline: none;
          background: none;
          padding: 0.5rem 0.7rem;
          font-size: 1.1rem;
          color: #fff;
          font-family: 'Saira', sans-serif;
        }

        .login-input::placeholder {
          color: transparent;
        }

        .login-input:focus::placeholder {
          color: #ADA4A4;
        }

        /* Toggle de contraseña */
        .password-toggle {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #ADA4A4;
          cursor: pointer;
          padding: 5px;
          z-index: 10;
          transition: color 0.3s ease;
        }

        .password-toggle:hover {
          color: #fe7217;
        }

        /* Checkbox personalizado */
        .remember-me {
          text-align: left;
          margin: 15px 0;
        }

        .remember-me label {
          color: #ADA4A4;
          font-size: 0.9rem;
          display: flex;
          align-items: center;
          cursor: pointer;
          transition: color 0.3s ease;
        }

        .remember-me label:hover {
          color: #fff;
        }

        .remember-me input {
          margin-right: 8px;
          accent-color: #fe7217;
        }

        /* Enlaces */
        .forgot-password {
          display: block;
          text-align: right;
          text-decoration: none;
          color: #999;
          font-size: 0.9rem;
          transition: color 0.3s ease;
          margin: 10px 0;
        }

        .forgot-password:hover {
          color: #38d39f;
        }

        /* Botón principal - Replicando exactamente el CSS original */
        .btn {
          display: block;
          width: 100%;
          height: 50px;
          outline: none;
          border: none;
          background-image: linear-gradient(to right, #fdcf11, #ff7318, #ff7318);
          background-size: 200%;
          font-size: 0.8rem;
          color: #fff;
          font-family: 'Saira', sans-serif;
          text-transform: uppercase;
          margin: 1rem 0;
          cursor: pointer;
          transition: all 0.5s ease;
          border-radius: 4px;
          font-weight: 600;
          
        }

        .btn:hover:not(:disabled) {
          background-position: right;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(255, 115, 24, 0.4);
        }

        .btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        /* Botón demo */
        .demo-btn {
          display: inline-flex;
          align-items: center;
          padding: 8px 16px;
          background: rgba(255, 255, 255, 0.1);
          color: #ADA4A4;
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          font-size: 0.8rem;
          cursor: pointer;
          margin-bottom: 20px;
          transition: all 0.3s ease;
          text-decoration: none;
        }

        .demo-btn:hover {
          background: rgba(255, 255, 255, 0.2);
          color: #fff;
          transform: translateY(-1px);
        }

        /* Mensaje de error */
        .error-message {
          background: rgba(220, 38, 38, 0.1);
          border: 1px solid rgba(220, 38, 38, 0.3);
          color: #fca5a5;
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 20px;
          font-size: 0.9rem;
          animation: shake 0.5s ease-in-out;
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }

        /* Info demo */
        .demo-info {
          margin-top: 20px;
          padding: 15px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 8px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
        }

        .demo-info h4 {
          color: #fce148;
          font-size: 0.8rem;
          margin-bottom: 8px;
          font-weight: 600;
        }

        .demo-info p {
          font-size: 0.7rem;
          color: #ADA4A4;
          margin: 2px 0;
        }

        /* Pestaña decorativa */
        .pestana {
          display: flex;
          justify-content: center;
          border-style: solid;
          border-width: 0px 100px 0px 0px;
          border-color: #FF7318;
        }

        /* Footer */
        .site-footer {
          position: relative;
          width: 100%;
          padding: 1rem 2rem;
          background: rgba(0, 0, 0, 0.3);
          backdrop-filter: blur(5px);
          z-index: 10;
          margin-top: auto;
        }

        .footer-content {
          width: 100%;
          max-width: 1200px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin: 0 auto;
        }

        .version, .copyright {
          color: #ADA4A4;
          font-size: 0.8rem;
          margin: 0;
          font-family: 'Saira', sans-serif;
        }

        /* Animaciones adicionales */
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        /* RESPONSIVE DESIGN - Replicando exactamente el CSS original */
        @media screen and (max-width: 992px) {
          .login-box {
            padding: 4em 3em;
          }
          
          .login-form {
            padding-right: 100px;
          }
        }

        @media screen and (max-width: 768px) {
          .login-box {
            flex-direction: column;
            padding: 2em 1.5em;
            padding-bottom: 6em;
          }
          
          .login-img {
            display: none;
          }
          
          .login-form {
            padding-right: 0;
            width: 100%;
          }
          
          .pestana {
            display: none;
          }
          
          form {
            width: 100%;
          }
          
          .site-footer {
            position: fixed;
            bottom: 0;
            padding: 0.8rem 1rem;
          }
          
          .footer-content {
            flex-direction: column;
            text-align: center;
            gap: 0.3rem;
          }
        }

        @media screen and (max-width: 480px) {
          .login-box {
            padding: 1.5em 1em;
            padding-bottom: 5em;
          }
          
          .logo img {
            height: 80px;
          }
          
          .title {
            font-size: 0.8rem;
            margin-bottom: 20px;
          }
          
          .login-input {
            font-size: 1rem;
          }
          
          .btn {
            height: 45px;
            font-size: 1rem;
          }
          
          .site-footer {
            padding: 0.6rem 0.8rem;
          }
          
          .version, .copyright {
            font-size: 0.7rem;
          }
        }

        @media screen and (max-height: 600px) {
          .login-box {
            padding-top: 1.5em;
            padding-bottom: 1.5em;
          }
          
          .logo img {
            height: 70px;
          }
        }
        
.register-link {
  text-align: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: #ADA4A4;
  font-size: 0.9rem;
}

.register-link a {
  color: #fce148;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  margin-left: 5px;
}

.register-link a:hover {
  color: #fe7217;
  text-decoration: underline;
}
      `}</style>

      <div className="login-container">
        <div className="login-box">
          {/* Columna izquierda: imagen */}
          <div className="login-img">
            <img src={leftImage} alt="Imagen lateral" />
              
         </div>
  
          {/* Columna derecha: formulario */}
          <div className="login-form">
            <form className="form-width" onSubmit={handleSubmit}>
              {/* Logo - Usando logo_light.png para fondo oscuro */}
              <div className="logo">
                <img src={logoLight} alt="Logo KIKAI" />
              </div>
                          
            
              {/* Mensaje de error */}
              {error && (
                <div className="error-message">
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ 
                      width: '8px', 
                      height: '8px', 
                      backgroundColor: '#ef4444', 
                      borderRadius: '50%', 
                      marginRight: '8px',
                      animation: 'pulse 1s infinite'
                    }}></div>
                    {error}
                  </div>
                </div>
              )}
              
              

              {/* Input Usuario/Email */}
              <div className="input-div one">
                <div className="i">
                  <User />
                </div>
                <div>
                  <h5>Usuario</h5>
                  <input 
                    type="email" 
                    className="login-input" 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required 
                    disabled={loading}
                    autoComplete="email"
                  />
                </div>
              </div>

              {/* Input Contraseña */}
              <div className="input-div pass">
                <div className="i"> 
                  <Lock />
                </div>
                <div>
                  <h5>Contraseña</h5>
                  <input 
                    type={showPassword ? 'text' : 'password'} 
                    className="login-input" 
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required 
                    disabled={loading}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Checkbox Recordarme */}
              <div className="remember-me">
                <label>
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={loading}
                  />
                  Recordarme
                </label>
              </div>

              {/* Enlace olvidó contraseña */}
              <a href="#" className="forgot-password">¿Olvidó su contraseña?</a>
              
              {/* Botón de envío */}
              <button 
                type="submit" 
                className="btn" 
                disabled={loading}
              >
                {loading ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #ffffff',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    CARGANDO...
                  </span>
                ) : (
                  'INGRESAR'
                )}
              </button>

                 {/* ✅ BOTÓN DE PAGO (solo si hay URL) */}
              {paymentUrl && (
                <button
                  type="button"
                  className="btn-payment"
                  onClick={() => window.location.href = paymentUrl}
                >
                  <CreditCard />
                  IR A PAGAR
                </button>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '20px' }}>
              <button
                type="button"
                data-tooltip="Formulario para solicitar sistemas de gestión"
                className="btn-promo"
                onClick={() => setShowSystemsModal(true)}
              >
                <span>🔧</span>
                <span>Sistemas de gestión</span>
              </button>
            
              <button
                type="button"
                data-tooltip="Formulario para cotizaciones de repuestos o piezas"
                className="btn-promo"
                onClick={() => setShowPartsModal(true)}
              >
                <span>🛠️</span>
                Cotiza Repuestos y equipamiento
              </button>
            </div>
        
              <div className="register-link">
                ¿No tienes cuenta?
                <Link to="/register">Regístrate aquí</Link>
              </div>
           
            </form>
          </div>
          
          {/* Pestaña decorativa */}
          <div className="pestana"></div>
        </div>

        {/* Footer */}
        <footer className="site-footer">
          {/* NUEVA SECCIÓN INFORMATIVA */}
              <div className="info-section">
                     <div className="info-item">
                      
                       <div className="info-text">
                         <strong>Sistemas de gestión</strong>
                         <p>Instalación, mantención y soporte técnico para sistemas de monitoreo</p>
                       </div>
                     </div>
                  
                       <div className="info-item">
                      
                         <div className="info-text">
                           <strong>Cotizaciones de Repuestos y equipamiento</strong>
                           <p>Con centros logísticos y proveedores internacionales, nuestro equipo trabaja 24/7 para asegurar cobertura completa de partes y entregas rápidas.</p>
                         </div>
                       </div>
                </div>
          <div className="footer-content">
            <p className="version">Versión 1.4.0</p>
            <p className="copyright">© 2026 KIKAI. Todos los derechos reservados.</p>
          </div>
        </footer>
      </div>
      
        {/* MODAL SISTEMAS */}
          {/* MODALES */}
              <QuotePartsModal 
                isOpen={showPartsModal}
                onClose={() => setShowPartsModal(false)}
              />
        
              <QuoteSystemsModal 
                isOpen={showSystemsModal}
                onClose={() => setShowSystemsModal(false)}
              />
{/* MODAL AYUDA NP */}
{showNPHelper && (
  <div className="modal-overlay" onClick={() => setShowNPHelper(false)}>
    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
      <div className="modal-header">
        <h3>🔍 Ayuda para Encontrar Número de Parte</h3>
        <button className="modal-close" onClick={() => setShowNPHelper(false)}>
          <X />
        </button>
      </div>
      
      <div className="np-helper-content">
        <div className="help-section">
          <h4>📍 ¿Dónde encontrar el NP?</h4>
          <ul>
            <li>En la etiqueta del repuesto original</li>
            <li>En el manual de partes del equipo</li>
            <li>En la placa del motor</li>
            <li>Formato típico: XXX-XXXX</li>
          </ul>
        </div>
        
        <div className="help-section">
          <h4>🔧 Números comunes:</h4>
          {Object.entries(NUMEROS_PARTE_COMUNES).map(([categoria, partes]) => (
            <div key={categoria} className="categoria-np">
              <strong>{categoria}:</strong>
              <ul>
                {partes.map(parte => (
                  <li key={parte.np}>
                    <code>{parte.np}</code> - {parte.desc}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        
        <button className="btn-modal-submit" onClick={() => setShowNPHelper(false)}>
          Entendido
        </button>
      </div>
    </div>
  </div>
)} 
      
    </>
  );
  
};

export default Login;